import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  isRedirect,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { AuditLogsPage } from './pages/AuditLogsPage';
import { AmazonPage } from './pages/app/AmazonPage';
import { DashboardPage } from './pages/app/DashboardPage';
import { DossierPage } from './pages/app/DossierPage';
import { FeedPage } from './pages/app/FeedPage';
import { FinancePage } from './pages/app/FinancePage';
import { FeeCenterPage } from './pages/app/FeeCenterPage';
import { ExpensesPage } from './pages/app/ExpensesPage';
import { PipelinePage } from './pages/app/PipelinePage';
import { OrdersPage } from './pages/app/OrdersPage';
import { PlanningPage } from './pages/app/PlanningPage';
import { PriceCalibrationPage } from './pages/app/PriceCalibrationPage';
import { ResearchPage } from './pages/app/ResearchPage';
import { ReceivingPage } from './pages/app/ReceivingPage';
import { WarehousePrepPage } from './pages/app/WarehousePrepPage';
import { WarehouseBillingPage } from './pages/app/WarehouseBillingPage';
import { ReportsPage } from './pages/app/ReportsPage';
import { SalesPage } from './pages/app/SalesPage';
import { ShipmentsPage } from './pages/app/ShipmentsPage';
import { SettingsPage } from './pages/app/SettingsPage';
import { BrandDetailPage } from './pages/BrandDetailPage';
import { BrandsPage } from './pages/BrandsPage';
import { CredentialsPage } from './pages/CredentialsPage';
import { DesignPreview } from './pages/DesignPreview';
import { FeeProfileEditPage } from './pages/FeeProfileEditPage';
import { FeeProfilesPage } from './pages/FeeProfilesPage';
import { InboxPage } from './pages/InboxPage';
import { PricingSheetDetailPage } from './pages/PricingSheetDetailPage';
import { ProductDetailPage } from './pages/ProductDetailPage';
import { ProductsPage } from './pages/ProductsPage';
import { RunsPage } from './pages/RunsPage';
import { SchedulesPage } from './pages/SchedulesPage';
import { SearchPage } from './pages/SearchPage';
import { SigninPage } from './pages/SigninPage';
import { SourcingPage } from './pages/SourcingPage';
import { SpApiWizardPage } from './pages/SpApiWizardPage';
import { SupplierDetailPage } from './pages/SupplierDetailPage';
import { SuppliersPage } from './pages/SuppliersPage';
import { TemplatesPage } from './pages/TemplatesPage';
import { meQuery } from './lib/auth';
import { canAccessFeeCenter } from './lib/fee-center';

interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
});

// The root is a router, not a page: signed-in visitors land on the app
// home; everyone else signs in. The legacy Phase-1 SearchPage lives on at
// /search (unlinked, ops-only) until the old ops routes are retired.
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
    throw redirect({ to: '/app' });
  },
  component: () => null,
});

const searchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/search',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: SearchPage,
});

const signinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signin',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
      throw redirect({ to: '/app' });
    } catch (err) {
      if (isRedirect(err)) throw err;
      // Not signed in (401 from /api/me) — proceed to render the form.
    }
  },
  component: SigninPage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: DashboardPage,
});

const brandsFeedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/brands',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: FeedPage,
});

const dossierRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/brands/$brandCacheId',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: DossierPage,
});

const pipelineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/pipeline',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: PipelinePage,
});

const ordersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/orders',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: OrdersPage,
});

const amazonRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/amazon',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: AmazonPage,
});

const receivingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/receiving',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: ReceivingPage,
});

const warehousePackingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/warehouse/packing',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: () => <WarehouseBillingPage section="packing" />,
});

const warehousePrepRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/warehouse/prep',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: WarehousePrepPage,
});

const warehouseChargesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/warehouse/charges',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: () => <WarehouseBillingPage section="charges" />,
});

const warehouseInvoicesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/warehouse/invoices',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: () => <WarehouseBillingPage section="invoices" />,
});

const shipmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/shipments',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: ShipmentsPage,
});

const planningRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/planning',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: PlanningPage,
});

const researchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/research',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: ResearchPage,
});

const priceCalibrationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/price-calibration',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: PriceCalibrationPage,
});

const salesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/sales',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: SalesPage,
});

const financeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/finance',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: FinancePage,
});

const feeCenterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/finance/fee-center',
  beforeLoad: async ({ context }) => {
    try {
      const me = await context.queryClient.ensureQueryData(meQuery);
      if (me.user.role === 'warehouse') throw redirect({ to: '/app/receiving' });
      if (!canAccessFeeCenter(me.user.role, me.modules?.amazon === true)) {
        throw redirect({ to: '/app/finance' });
      }
    } catch (err) {
      if (isRedirect(err)) throw err;
      throw redirect({ to: '/signin' });
    }
  },
  component: FeeCenterPage,
});

const expensesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/expenses',
  beforeLoad: async ({ context }) => {
    try {
      const me = await context.queryClient.ensureQueryData(meQuery);
      if (me.user.role !== 'owner') throw redirect({ to: '/app/finance' });
    } catch (err) {
      if (isRedirect(err)) throw err;
      throw redirect({ to: '/signin' });
    }
  },
  component: ExpensesPage,
});

const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/reports',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: ReportsPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/app/settings',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: SettingsPage,
});

const designRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/design',
  component: DesignPreview,
});

const credentialsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/credentials',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: CredentialsPage,
});

const feeProfilesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/fee-profiles',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: FeeProfilesPage,
});

const feeProfileEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/fee-profiles/$id',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: FeeProfileEditPage,
});

const auditLogsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/audit-logs',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: AuditLogsPage,
});

const productsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/products',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: ProductsPage,
});

const productDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/products/$asin',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: ProductDetailPage,
});

const brandsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/brands',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: BrandsPage,
});

const brandDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/brands/$name',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: BrandDetailPage,
});

const sourcingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sourcing',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: SourcingPage,
});

const suppliersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/suppliers',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: SuppliersPage,
});

const supplierDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/suppliers/$id',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: SupplierDetailPage,
});

const pricingSheetDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pricing-sheets/$id',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: PricingSheetDetailPage,
});

const runsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/runs',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: RunsPage,
});

const schedulesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/schedules',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: SchedulesPage,
});

const templatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/templates',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: TemplatesPage,
});

const spApiWizardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/sp-api',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: SpApiWizardPage,
});

const inboxRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/inbox',
  beforeLoad: async ({ context }) => {
    try {
      await context.queryClient.ensureQueryData(meQuery);
    } catch {
      throw redirect({ to: '/signin' });
    }
  },
  component: InboxPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  searchRoute,
  appRoute,
  brandsFeedRoute,
  dossierRoute,
  pipelineRoute,
  ordersRoute,
  amazonRoute,
  receivingRoute,
  warehousePrepRoute,
  warehousePackingRoute,
  warehouseChargesRoute,
  warehouseInvoicesRoute,
  shipmentsRoute,
  planningRoute,
  researchRoute,
  priceCalibrationRoute,
  salesRoute,
  financeRoute,
  feeCenterRoute,
  expensesRoute,
  reportsRoute,
  settingsRoute,
  signinRoute,
  designRoute,
  credentialsRoute,
  feeProfilesRoute,
  feeProfileEditRoute,
  auditLogsRoute,
  productsRoute,
  productDetailRoute,
  brandsRoute,
  brandDetailRoute,
  sourcingRoute,
  suppliersRoute,
  supplierDetailRoute,
  pricingSheetDetailRoute,
  runsRoute,
  schedulesRoute,
  templatesRoute,
  spApiWizardRoute,
  inboxRoute,
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
  });
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}
